import { Box } from "@mui/material";
import { useUser } from "@/app/providers";
import React, { useEffect, useMemo } from "react";
import { CustomerDropdownAutoSave } from "@/features";
import { useCompanies, useUpdateProfile } from "@/hooks";
import { object } from "prop-types";
import type { SxProps, Theme } from "@mui/material/styles";

type CustomerSelectorProps = {
  sx?: SxProps<Theme>;
};

/**
 *
 */
export const CustomerSelector = (props: CustomerSelectorProps) => {
  const { user, setUser } = useUser();
  const { currentTenantId } = user || {};

  const allCompaniesVars = useMemo(() => {
    return currentTenantId === "all"
      ? { customerType: "parent" as const }
      : { customerId: "__none__" };
  }, [currentTenantId]);

  const userCompaniesVars = useMemo(() => {
    return currentTenantId && currentTenantId !== "all"
      ? { customerType: "parent" as const, tenantId: currentTenantId }
      : { customerId: "__none__" };
  }, [currentTenantId]);

  const { customers: allCompanies } = useCompanies(allCompaniesVars);
  const { customers: userCompanies } = useCompanies(userCompaniesVars);

  const companies = useMemo(() => {
    if (currentTenantId === "all") {
      return allCompanies;
    } else if (currentTenantId) {
      return userCompanies;
    }
    return [];
  }, [allCompanies, currentTenantId, userCompanies]);

  const boxSx = useMemo(() => {
    return {
      ...props.sx,
      ml: 2,
    };
  }, [props.sx]);

  const { updateProfile } = useUpdateProfile();

  useEffect(() => {
    if (companies?.length) {
      const targetKey = user?.currentCustomerKey;
      const targetId = user?.currentTeamId;
      const targetName = user?.currentTeam;

      const foundRow = companies.find((row: any) => {
        if (targetKey) {
          const tKey = String(targetKey).trim().toUpperCase();
          if (row.key && String(row.key).toUpperCase() === tKey) return true;
          if (row.customerKey && String(row.customerKey).toUpperCase() === tKey)
            return true;
          if (row.id && String(row.id) === targetKey) return true;
          if (
            row.country &&
            row.cvr &&
            `${row.country}${row.cvr}`.toUpperCase() === tKey
          )
            return true;
        }
        if (targetId && String(row.id) === String(targetId)) return true;
        if (targetName && row.name === targetName) return true;
        return false;
      });

      if (foundRow) {
        const rowKey =
          (foundRow as any).key ||
          (foundRow.country && foundRow.cvr
            ? `${foundRow.country.toUpperCase()}${foundRow.cvr}`
            : (foundRow as any).customerKey) ||
          foundRow.id;

        if (
          user?.currentTeam !== foundRow.name ||
          user?.currentTeamId !== foundRow.id ||
          !user?.currentCustomerKey
        ) {
          setUser((prev: any) => ({
            ...prev,
            currentCustomerKey: prev?.currentCustomerKey || rowKey,
            currentTeam: foundRow.name,
            currentTeamId: foundRow.id,
          }));
        }
      } else if (!targetKey && !targetId && !targetName) {
        const fallback = companies[0];
        const fallbackKey =
          (fallback as any).key ||
          (fallback.country && fallback.cvr
            ? `${fallback.country.toUpperCase()}${fallback.cvr}`
            : (fallback as any).customerKey) ||
          fallback.id;

        setUser((prev: any) => ({
          ...prev,
          currentCustomerKey: fallbackKey,
          currentTeam: fallback.name,
          currentTeamId: fallback.id,
        }));
        updateProfile({
          currentCustomerKey: fallbackKey,
        });
      }
    }
  }, [
    companies,
    user?.currentCustomerKey,
    user?.currentTeamId,
    user?.currentTeam,
    setUser,
    updateProfile,
  ]);

  return (
    <Box sx={boxSx}>{companies.length > 0 && <CustomerDropdownAutoSave />}</Box>
  );
};

CustomerSelector.propTypes = {
  sx: object,
};
